import {
  apiErrorSchema,
  type PlaceDto,
  type SearchPlacesResponse,
  searchPlacesResponseSchema,
  type TakeDownSpotResponse,
  takeDownSpotResponseSchema,
  type DeclareSpotBody,
  type ListSpotsQuery,
  type ListSpotsResponse,
  listSpotsResponseSchema,
  type ReportSpotResponse,
  reportSpotResponseSchema,
  type SpotDto,
  spotSchema,
} from "@boomap/contracts";

/** Erreur métier renvoyée par l'API, avec son code stable et son contexte. */
export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;

  constructor(input: {
    code: string;
    message: string;
    status: number;
    details?: Record<string, unknown>;
  }) {
    super(input.message);
    this.name = "ApiRequestError";
    this.code = input.code;
    this.status = input.status;
    this.details = input.details ?? {};
  }
}

const BASE_URL = "/api";

const request = async (path: string, init?: RequestInit): Promise<unknown> => {
  // `Headers` se compose, ne se répand pas : un spread d'objet perdrait
  // silencieusement les en-têtes déjà présents dans `init`.
  const headers = new Headers(init?.headers);

  if (init?.body !== undefined) {
    headers.set("content-type", "application/json");
  }

  const response = await fetch(`${BASE_URL}${path}`, { ...init, headers });

  const payload: unknown = response.status === 204 ? null : await response.json();

  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(payload);

    throw new ApiRequestError({
      code: parsed.success ? parsed.data.error.code : "unexpected_response",
      message: parsed.success ? parsed.data.error.message : "L'API a répondu de façon inattendue.",
      status: response.status,
      ...(parsed.success && parsed.data.error.details !== undefined
        ? { details: parsed.data.error.details }
        : {}),
    });
  }

  return payload;
};

export const listSpots = async (
  area: ListSpotsQuery,
  signal?: AbortSignal,
): Promise<ListSpotsResponse> => {
  const query = new URLSearchParams({
    south: String(area.south),
    west: String(area.west),
    north: String(area.north),
    east: String(area.east),
  });

  return listSpotsResponseSchema.parse(
    await request(`/spots?${query.toString()}`, signal === undefined ? {} : { signal }),
  );
};

export const declareSpot = async (body: DeclareSpotBody): Promise<SpotDto> =>
  spotSchema.parse(await request("/spots", { method: "POST", body: JSON.stringify(body) }));

export const reportSpot = async (id: string): Promise<ReportSpotResponse> =>
  reportSpotResponseSchema.parse(await request(`/spots/${id}/reports`, { method: "POST" }));

/** Rétractation par l'auteur : la pastille est effacée, l'emplacement libéré. */
export const deleteSpot = async (id: string): Promise<void> => {
  await request(`/spots/${id}`, { method: "DELETE" });
};

/** Retrait demandé par l'occupant du logement : effet immédiat. */
export const takeDownSpot = async (id: string): Promise<TakeDownSpotResponse> =>
  takeDownSpotResponseSchema.parse(await request(`/spots/${id}/takedown`, { method: "POST" }));

/**
 * `language` est la langue choisie dans l'application, pas celle du navigateur :
 * l'API la transmet à l'annuaire, qui renvoie des libellés localisés. Sans cet
 * en-tête explicite, chercher « Bordeaux » en anglais rendrait des noms français.
 */
export const searchPlaces = async (
  query: string,
  language: string,
  signal?: AbortSignal,
): Promise<readonly PlaceDto[]> => {
  const search = new URLSearchParams({ q: query });
  const response: SearchPlacesResponse = searchPlacesResponseSchema.parse(
    await request(`/places?${search.toString()}`, {
      headers: { "accept-language": language },
      ...(signal === undefined ? {} : { signal }),
    }),
  );

  return response.places;
};
